/**
 * Sessions and participants (SPEC §6).
 *
 * The Google Sheet is read-only and also feeds a website, so raw rows are read
 * through a mapping layer that only looks at the columns this project uses.
 * The normalization from raw rows to {@link Session} / {@link Participant} lives
 * in the graphics app (Phase 2); this module defines the shapes on both sides.
 */
import { z } from 'zod';

/** Maximum participants per session, host included (SPEC §1). */
export const MAX_PARTICIPANTS = 6;

/** Minimum participants for a `MAIN` layout (host plus one speaker, SPEC §5.3). */
export const MIN_MAIN_PARTICIPANTS = 2;

/** The literal value of `speaker_order` that flags the host (SPEC §6). */
export const HOST_SPEAKER_ORDER = 'h';

/** Case-, accent- and whitespace-insensitive form of a name, for matching. */
export function normalizeName(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

const id = z.string().trim().min(1).max(128);
const text = z.string().max(2000);

export const SessionTypeSchema = z.enum(['session', 'workshop', 'break']);
export type SessionType = z.infer<typeof SessionTypeSchema>;

export const SessionStatusSchema = z.enum(['confirmed', 'cancelled']);
export type SessionStatus = z.infer<typeof SessionStatusSchema>;

/** A normalized row of the Sessions tab. */
export const SessionSchema = z.strictObject({
  sessionId: id,
  title: text,
  type: SessionTypeSchema,
  status: SessionStatusSchema,
  day: text,
  startTime: text,
  endTime: text,
  track: text,
  theme: text,
  location: text.optional(),
  description: text.optional(),
  tags: z.array(z.string().max(200)).max(50).optional(),
});
export type Session = z.infer<typeof SessionSchema>;

export const ParticipantRoleSchema = z.enum(['host', 'participant']);
export type ParticipantRole = z.infer<typeof ParticipantRoleSchema>;

/**
 * A normalized row of the Speakers tab.
 *
 * `participantId` is generated by the graphics app (from `session_id` + normalized
 * name, persisted in the project store), never the display name. Never key state on a name.
 */
export const ParticipantSchema = z
  .strictObject({
    participantId: id,
    displayName: z.string().trim().min(1).max(200),
    organization: z.string().max(200),
    jobTitle: z.string().max(200),
    /** `'host'` iff the sheet's `speaker_order` is `h`. */
    role: ParticipantRoleSchema,
    /** Sort order from `speaker_order`; `null` for the host. */
    order: z.number().int().min(0).nullable(),
    /** Learned Zoom display names, persisted per project. */
    aliases: z.array(z.string().max(200)).max(50),
  })
  .refine((p) => (p.role === 'host') === (p.order === null), {
    message: 'role must be "host" exactly when order is null',
    path: ['order'],
  });
export type Participant = z.infer<typeof ParticipantSchema>;

/**
 * Raw Sessions-tab row as read from the sheet, keyed by header name.
 * Not strict: the sheet has columns used by the website that this project ignores.
 */
export const SheetSessionRowSchema = z.object({
  session_id: z.string(),
  title: z.string(),
  type: z.string(),
  status: z.string(),
  day: z.string(),
  start_time: z.string(),
  end_time: z.string(),
  track: z.string(),
  theme: z.string(),
  location: z.string().optional(),
  description: z.string().optional(),
  tags: z.string().optional(),
});
export type SheetSessionRow = z.infer<typeof SheetSessionRowSchema>;

/**
 * Raw Speakers-tab row. `title` is the person's job title (renamed `jobTitle` on import).
 * `speaker_order` is numeric, or `h` for the host.
 */
export const SheetSpeakerRowSchema = z.object({
  session_id: z.string(),
  speaker_name: z.string(),
  organization: z.string(),
  title: z.string(),
  speaker_order: z.string(),
});
export type SheetSpeakerRow = z.infer<typeof SheetSpeakerRowSchema>;
